import type {
  FieldDef,
  FormSchema,
  QuestionDef,
} from "../risikoanalyseStep/types";

/**
 * Schema for the Maschinen risk step form.
 *
 * Field keys mirror the legacy MachineRiskItem shape so saved characteristics
 * stay compatible with anything that already consumes them.
 */

const yn = (
  question: string,
  key: string,
  followUp?: FieldDef[],
  hint?: string,
): QuestionDef => ({
  question,
  hint,
  field: { key, type: "yesno" },
  ...(followUp
    ? {
        showFollowUpWhen: { key, equals: "ja" },
        followUpFields: followUp,
      }
    : {}),
});

const q = (question: string, field: FieldDef, hint?: string): QuestionDef => ({
  question,
  hint,
  field,
});

const qMulti = (
  question: string,
  fields: FieldDef[],
  opts?: { hint?: string; layout?: "one" | "two" },
): QuestionDef => ({
  question,
  hint: opts?.hint,
  layout: opts?.layout,
  fields,
});

export const maschinenStepFormSchema: FormSchema = {
  sections: [
    {
      key: "stammdaten",
      title: "Stammdaten",
      color: "pillarOrange",
      subSteps: [
        {
          key: "identifikation",
          title: "Identifikation",
          description:
            "Bitte erfassen Sie die wichtigsten Informationen zur Maschine.",
          questions: [
            q(
              "Wie soll die Maschine intern benannt werden?",
              {
                key: "label",
                label: "Bezeichnung",
                type: "text",
                placeholder: "z. B. Maschine 1",
              },
              "Eine interne Kurzbezeichnung — wird in der Risikoliste angezeigt.",
            ),
            q("Maschinenbeschreibung", {
              key: "machineDescription",
              label: "Beschreibung",
              type: "textarea",
              placeholder: "Funktion, Einsatzgebiet, Besonderheiten …",
            }),
            qMulti(
              "Hersteller und Typ",
              [
                {
                  key: "manufacturer",
                  label: "Hersteller",
                  type: "text",
                  placeholder: "z. B. Trumpf, DMG MORI …",
                },
                {
                  key: "type",
                  label: "Typenbezeichnung",
                  type: "text",
                  placeholder: "z. B. TruLaser 3030",
                },
              ],
              { layout: "two" },
            ),
            qMulti(
              "Nummern und Kennzeichnung",
              [
                {
                  key: "machineNumber",
                  label: "Maschinennummer",
                  type: "text",
                },
                {
                  key: "serialNumber",
                  label: "Seriennummer",
                  type: "text",
                },
                {
                  key: "internalDeviceName",
                  label: "Interne Gerätebezeichnung",
                  type: "text",
                },
                {
                  key: "internalDeviceNumber",
                  label: "Interne Gerätenummer",
                  type: "text",
                },
              ],
              { layout: "two" },
            ),
            qMulti(
              "Sonstige Hinweise / Kennzeichen",
              [
                { key: "plateNumber", label: "Kennzeichen (falls vorhanden)", type: "text" },
                { key: "other", label: "Sonstiges", type: "textarea" },
              ],
              { layout: "two" },
            ),
          ],
        },
        {
          key: "einsatz",
          title: "Einsatz & Bauart",
          description:
            "Stationäre oder mobile Maschine, Zustand bei Anschaffung.",
          questions: [
            q("Wie ist die Maschine im Einsatz?", {
              key: "machineType",
              label: "Bauart",
              type: "select",
              options: [
                { value: "stationär", label: "Stationär" },
                { value: "mobil", label: "Mobil" },
              ],
            }),
            q("Zustand bei Anschaffung", {
              key: "conditionAtPurchase",
              label: "Zustand",
              type: "select",
              options: [
                { value: "neu", label: "Neu" },
                { value: "gebraucht", label: "Gebraucht" },
              ],
            }),
            q("Baujahr", {
              key: "yearBuilt",
              label: "Baujahr",
              type: "number",
              placeholder: "z. B. 2022",
            }),
            q("Listenpreis (Neu- bzw. Wiederbeschaffungswert)", {
              key: "listPriceNewOrBuild",
              label: "Listenpreis",
              type: "text",
              placeholder: "z. B. 125.000",
            }),
            yn(
              "Gibt es spezielle Anbauten oder Zubehör?",
              "hasSpecialAttachments",
              [
                {
                  key: "specialAttachments",
                  label: "Welche Anbauten / Zubehör?",
                  type: "textarea",
                },
              ],
            ),
          ],
        },
      ],
    },
    {
      key: "anschaffung",
      title: "Anschaffung & Finanzierung",
      color: "blue",
      subSteps: [
        {
          key: "anschaffung",
          title: "Anschaffung",
          description: "Daten zur Anschaffung der Maschine.",
          questions: [
            qMulti(
              "Anschaffungsart & -datum",
              [
                {
                  key: "acquisitionType",
                  label: "Art",
                  type: "select",
                  options: [
                    { value: "kauf", label: "Kauf" },
                    { value: "mietkauf", label: "Mietkauf" },
                    { value: "leasing", label: "Leasing" },
                  ],
                },
                {
                  key: "acquisitionDate",
                  label: "Anschaffungsdatum",
                  type: "date",
                },
              ],
              { layout: "two" },
            ),
            q(
              "Wie wurde die Maschine gekauft?",
              {
                key: "purchaseFunding",
                label: "Finanzierungsweise",
                type: "select",
                options: [
                  { value: "eigenmittel", label: "Eigenmittel" },
                  { value: "finanzierung", label: "Finanzierung" },
                ],
              },
              "Nur ausfüllen, wenn die Anschaffungsart „Kauf“ ist.",
            ),
            qMulti(
              "Kaufpreis und Datum",
              [
                { key: "purchasePrice", label: "Kaufpreis", type: "text" },
                { key: "purchaseDate", label: "Kaufdatum", type: "date" },
              ],
              { layout: "two" },
            ),
            qMulti(
              "Finanzierungspartner und Laufzeit",
              [
                {
                  key: "purchaseFinancingPartner",
                  label: "Bank / Finanzierungspartner",
                  type: "text",
                },
                {
                  key: "purchaseFinancingTerm",
                  label: "Laufzeit (Monate)",
                  type: "number",
                },
              ],
              { layout: "two" },
            ),
          ],
        },
        {
          key: "mietkauf_leasing",
          title: "Mietkauf / Leasing",
          description:
            "Ergänzende Angaben bei Mietkauf oder Leasing.",
          questions: [
            qMulti(
              "Mietkauf",
              [
                { key: "hirePurchaseRate", label: "Mietkaufrate", type: "text" },
                {
                  key: "hirePurchaseTerm",
                  label: "Laufzeit Mietkauf (Monate)",
                  type: "number",
                },
              ],
              { layout: "two" },
            ),
            qMulti(
              "Leasing",
              [
                { key: "leasingRate", label: "Leasingrate", type: "text" },
                {
                  key: "leasingTerm",
                  label: "Laufzeit Leasing (Monate)",
                  type: "number",
                },
              ],
              { layout: "two" },
            ),
          ],
        },
      ],
    },
    {
      key: "versicherung",
      title: "Versicherung",
      color: "green",
      subSteps: [
        {
          key: "versicherung",
          title: "Versicherungsumfang",
          description: "Versicherungswunsch und Konditionen.",
          questions: [
            yn(
              "Soll die Maschine versichert werden?",
              "insuranceDesired",
              [
                {
                  key: "deductible",
                  label: "Selbstbeteiligung",
                  type: "text",
                  placeholder: "z. B. 500 EUR",
                },
                { key: "inclusion", label: "Eingeschlossen", type: "textarea" },
                { key: "exclusion", label: "Ausgeschlossen", type: "textarea" },
              ],
            ),
            q("Sonstige Bemerkungen / Notizen", {
              key: "notes",
              label: "Notizen",
              type: "textarea",
            }),
          ],
        },
      ],
    },
  ],
};
